import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import Apiurl from "../../Helpers/apiUrl";
import { handleApiError } from "../../Helpers/handleApiError ";

const getToken = () => localStorage.getItem("u_token");

export const fetchProject = createAsyncThunk(
  "project/fetchProject",
  async (
    {
      search,
      page,
      rowsPerPage,
      month,
      year,
      from,
      to,
      user,
      client,
      sort_by,
      sort_order,
      state,
      region,
      role,
    },
    { rejectWithValue }
  ) => {
    const token = getToken();
    if (!token)
      return rejectWithValue({
        message: "No token found, authorization failed.",
      });

    try {
      const response = await axios.get(
        `${Apiurl}admin/project/view-list?search=${search}&page=${page + 1
        }&limit=${rowsPerPage}&month=${month || ""}&year=${year || ""
        }&from=${from}&to=${to}&user=${user}&client=${client}&sort_by=${sort_by}&sort_order=${sort_order}&role=${role}&state=${state}&region=${region}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch projects." }
      );
    }
  }
);
export const fetchProjectReport = createAsyncThunk(
  "project/fetchProjectReport",
  async (
    {
      search,
      page,
      rowsPerPage,
      from,
      to,
      user,
      client,
      channel,
      collection,
      sort_by,
      sort_order,
      role,
      month,
      year,
    },
    { rejectWithValue }
  ) => {
    const token = getToken();
    const params = {
      search: search,
      page: page + 1,
      limit: rowsPerPage,
      fromDate: from,
      toDate: to,
      user: user,
      client: client,
      channel: channel,
      collection: collection,
      sort_by: sort_by,
      sort_order: sort_order,
      role: role,
      month: month,
      year: year,
    };

    if (!token)
      return rejectWithValue({
        message: "No token found, authorization failed.",
      });

    try {
      const response = await axios.get(
        `${Apiurl}admin/product/collection-quotation`,
        {
          params:params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch projects." }
      );
    }
  }
);
export const skuReport = createAsyncThunk(
  "project/skuReport",
  async (
    {
      search,
      page,
      rowsPerPage,
      from,
      to,
      user,
      client,
      channel,
      sku,
      category,
      segment,
      collection,
      sort_by,
      sort_order,
      role,
      month,
      year,
      region,
      state
    },
    { rejectWithValue }
  ) => {
    const token = getToken();

    const params = {
      search: search,
      page: page + 1,
      limit: rowsPerPage,
      fromDate: from,
      toDate: to,
      user: user,
      client: client,
      channel: channel,
      sku_code: sku,
      category: category,
      segment: segment,
      collection: collection,
      sort_by: sort_by,
      sort_order: sort_order,
      role: role,
      month: month,
      year: year,
      region: region,
      state: state
    };

    if (!token)
      return rejectWithValue({
        message: "No token found, authorization failed.",
      });

    try {
      const response = await axios.get(
        `${Apiurl}admin/sku-report`,
        {
          params:params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch projects." }
      );
    }
  }
);

const initialState = {
  stats: {},
  projectData: [],
  projectReportData: [],
  skuReportData: [],
  userOpt: [],
  clientOpt: [],
  collectionOpt: [],
  count: 0,
  status: "idle",
  error: null,
};

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProject.pending, (state) => {
        state.error = null;
        state.status = "pending";
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.projectData = action.payload.data || [];
        state.stats = action.payload.unique || {};
        state.clientOpt = (action.payload?.client || []).map((client) => ({
          value: client.id,
          label: client.client_name,
        }));

        state.userOpt = (action.payload?.user || []).map((user) => ({
          value: user.id,
          label: `${user.name} (${user.emp_code})`,
        }));

        state.collectionOpt = (action.payload?.collection || []).map((user) => ({
          value: user.id,
          label: `${user.name} (${user.emp_code})`,
        }));
        state.count = action.payload.total;
        state.status = "succeeded";
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.error = action.payload;
        state.status = "failed";
      })
      .addCase(fetchProjectReport.pending, (state) => {
        state.error = null;
        state.status = "pending";
      })
      .addCase(fetchProjectReport.fulfilled, (state, action) => {
        state.projectReportData = action.payload.data || [];
        state.stats = action.payload.unique || {};
        state.clientOpt = (action.payload?.client || []).map((client) => ({
          value: client.id,
          label: client.client_name,
        }));
        state.count = action.payload.total;
        state.status = "succeeded";
      })
      .addCase(fetchProjectReport.rejected, (state, action) => {
        state.error = action.payload;
        state.status = "failed";
      })
      .addCase(skuReport.pending, (state) => {
        state.error = null;
        state.status = "pending";
      })
      .addCase(skuReport.fulfilled, (state, action) => {
        state.skuReportData = action.payload.data || [];
        state.stats = action.payload.unique || {};
        state.count = action.payload.total;
        state.status = "succeeded";
      })
      .addCase(skuReport.rejected, (state, action) => {
        state.error = action.payload;
        state.status = "failed";
      })
  },
});

export default projectSlice.reducer;